import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Alert, Tag, Table, Button, Typography } from 'antd';
import {
    CameraOutlined,
    DollarOutlined,
    FileTextOutlined,
    FileImageOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title } = Typography;

const RadiologyDashboard = () => {
    const { t } = useTranslation(['radiology', 'common']);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        todayExams: 0,
        totalExams: 0,
        revenueUSD: 0,
        revenueIQD: 0,
        pendingUSD: 0,
        pendingIQD: 0,
        pendingCount: 0
    });
    const [recentExams, setRecentExams] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const today = new Date().toISOString().split('T')[0];

            const [todayExamRes, allExamRes, revenueUSDRes, revenueIQDRes, pendingRes] = await Promise.all([
                api.get(`/radiology/reports/examinations?startDate=${today}&endDate=${today}`),
                api.get('/radiology/reports/examinations'),
                api.get('/radiology/reports/revenue?currency=USD'),
                api.get('/radiology/reports/revenue?currency=IQD'),
                api.get('/radiology/reports/pending-payments')
            ]);

            const todayData = todayExamRes.data?.data || {};
            const allData = allExamRes.data?.data || {};
            const revenueUSD = revenueUSDRes.data?.data || {};
            const revenueIQD = revenueIQDRes.data?.data || {};
            const pendingData = pendingRes.data?.data || {};

            const examList = (allData.examinations || []).slice(0, 10);
            setRecentExams(examList);

            const pendingInvoices = pendingData.invoices || [];
            const pendUSD = pendingInvoices.filter(i => i.currency === 'USD').reduce((s, i) => s + (i.balanceDue || 0), 0);
            const pendIQD = pendingInvoices.filter(i => i.currency === 'IQD').reduce((s, i) => s + (i.balanceDue || 0), 0);

            setStats({
                todayExams: todayData.totalExaminations || 0,
                totalExams: allData.totalExaminations || 0,
                revenueUSD: revenueUSD.totalRevenue || 0,
                revenueIQD: revenueIQD.totalRevenue || 0,
                pendingUSD: pendUSD,
                pendingIQD: pendIQD,
                pendingCount: pendingData.totalPendingInvoices || 0
            });

            setError('');
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.response?.data?.message || t('radiology:messages.dashboardFetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('radiology:exams.patient'),
            dataIndex: 'patientName',
            key: 'patientName',
        },
        {
            title: t('radiology:exams.patientId'),
            dataIndex: 'patientId',
            key: 'patientId',
            render: (text) => text || '-'
        },
        {
            title: t('radiology:exams.examType'),
            dataIndex: 'examType',
            key: 'examType',
            render: (type) => <Tag color="blue">{type || 'N/A'}</Tag>
        },
        {
            title: t('radiology:exams.uploadDate'),
            dataIndex: 'uploadDate',
            key: 'uploadDate',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-'
        },
        {
            title: t('radiology:exams.fileName'),
            dataIndex: 'fileName',
            key: 'fileName',
            render: (text) => <span><FileImageOutlined style={{ marginRight: 4 }} />{text || '-'}</span>
        }
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip={t('loading')} />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>{t('radiology:dashboard.title')}</Title>

            {error && (
                <Alert
                    message={t('radiology:dashboard.error')}
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:dashboard.todayExams')}
                            value={stats.todayExams}
                            prefix={<CameraOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:dashboard.totalExams')}
                            value={stats.totalExams}
                            prefix={<CameraOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:dashboard.revenueUSD')}
                            value={stats.revenueUSD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                            precision={2}
                            formatter={(value) => `$${Number(value).toFixed(2)}`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:dashboard.revenueIQD')}
                            value={stats.revenueIQD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                            formatter={(value) => `${Number(value).toLocaleString()} IQD`}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('radiology:dashboard.pendingPayments')}
                            value={stats.pendingCount}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#faad14' }}
                            suffix={
                                <span style={{ fontSize: 14, color: '#999' }}>
                                    {stats.pendingUSD > 0 && ` $${stats.pendingUSD.toFixed(2)}`}
                                    {stats.pendingIQD > 0 && ` ${stats.pendingIQD.toLocaleString()} IQD`}
                                </span>
                            }
                        />
                    </Card>
                </Col>
            </Row>

            <div style={{ marginBottom: 24 }}>
                <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    onClick={() => navigate('/radiology/exams')}
                    style={{ marginRight: 8 }}
                >
                    {t('radiology:dashboard.uploadXRay')}
                </Button>
                <Button
                    onClick={() => navigate('/radiology/exams')}
                    style={{ marginRight: 8 }}
                >
                    {t('radiology:dashboard.viewAllExams')}
                </Button>
                <Button
                    onClick={() => navigate('/radiology/reports')}
                >
                    {t('radiology:dashboard.viewReports')}
                </Button>
            </div>

            <Card
                title={t('radiology:dashboard.recentExams')}
                extra={<Button type="link" onClick={() => navigate('/radiology/exams')}>{t('radiology:dashboard.viewAll')}</Button>}
            >
                <Table
                    columns={columns}
                    dataSource={recentExams}
                    rowKey={(record, index) => `${record.patientName}-${index}`}
                    pagination={false}
                    locale={{ emptyText: t('radiology:dashboard.noExamsFound') }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default RadiologyDashboard;
